/**
 * Admin - Broadcasts.
 *
 * Composes the announcement strip that appears under the Vera header for
 * everyone, or for named roles. Gated on platform.broadcast (global Admin or
 * Shell.Broadcast).
 *
 * Two things this page insists on, both deliberate:
 *   - An end date is required and defaults to a week out. Auto-expiry is what
 *     stops a banner becoming permanent wallpaper nobody reads.
 *   - The PHI warning is loud. A broadcast goes to a whole role unreviewed,
 *     and Ops.User is a wide net.
 *
 * Design of record: docs/design/broadcast/PLAN.md
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Spinner,
  Subtitle2,
  Switch,
  Textarea,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AlertRegular, DeleteRegular, PauseRegular } from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bffFetch } from '@shared/api';
import { veracityColors } from '@shared/ui';
import { groupedVeraRoles } from '@shared/auth';
import {
  BROADCAST_QUERY_KEY,
  defaultEnd,
  easternWallToIso,
  formatWhen,
  type BroadcastSeverity,
} from '@widgets/broadcast';

// =============================================================================
// API
// =============================================================================

interface Broadcast {
  id: string;
  message: string;
  linkUrl: string | null;
  linkLabel: string | null;
  severity: BroadcastSeverity;
  audienceKind: 'everyone' | 'roles';
  audienceRoles: string[];
  startsAt: string;
  endsAt: string;
  dismissible: boolean;
  revision: number;
  source: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

const MAX_MESSAGE_CHARS = 280;
const QUERY_KEY = ['admin', 'broadcasts'];

async function expectOk(res: Response): Promise<void> {
  if (res.ok) return;
  let message = `Request failed (${res.status})`;
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) message = body.error;
  } catch {
    // non-JSON error body - keep the status message
  }
  throw new Error(message);
}

async function fetchBroadcasts(): Promise<Broadcast[]> {
  const res = await bffFetch('/api/shell/broadcasts');
  await expectOk(res);
  const data = (await res.json()) as { broadcasts: Broadcast[] };
  return data.broadcasts;
}

// Broadcast windows are Eastern for everyone, composer and reader alike. See
// broadcastTime.ts for why, and for what it deliberately does NOT change.

function isLive(b: Broadcast): boolean {
  const now = Date.now();
  return Date.parse(b.startsAt) <= now && Date.parse(b.endsAt) > now;
}

// =============================================================================
// Styles
// =============================================================================

const SEVERITY_COLORS: Record<BroadcastSeverity, { bg: string; fg: string }> = {
  info: { bg: veracityColors.duskBlue, fg: veracityColors.firstLight },
  warning: { bg: veracityColors.daybreakAmber, fg: veracityColors.midnightCharcoal },
  critical: { bg: veracityColors.morningCoral, fg: veracityColors.midnightCharcoal },
};

const useStyles = makeStyles({
  page: { padding: tokens.spacingVerticalXXL, maxWidth: '1100px', margin: '0 auto' },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS,
  },
  titleIcon: { fontSize: '28px', color: veracityColors.veracityOrange },
  subtitle: { color: veracityColors.duskBlue, marginBottom: tokens.spacingVerticalL },
  warning: { marginBottom: tokens.spacingVerticalL },
  composer: { padding: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalXXL },
  row: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalM,
  },
  // Fluent v9 Dropdown inside a flex/grid row needs both of these or it
  // collapses to its content width.
  grow: { flex: '1 1 220px', minWidth: '0', width: '100%' },
  counter: { display: 'block', textAlign: 'right', color: veracityColors.duskBlue },
  preview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '6px 16px',
    fontSize: '13px',
    fontWeight: 500,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: tokens.spacingVerticalM,
  },
  roleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
    gap: tokens.spacingVerticalXS,
    marginTop: tokens.spacingVerticalS,
    maxHeight: '220px',
    overflowY: 'auto',
    padding: tokens.spacingVerticalS,
    border: `1px solid ${veracityColors.firstLight}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalL,
    alignItems: 'center',
  },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
  },
  itemBody: { flex: 1, minWidth: 0 },
  meta: { color: veracityColors.duskBlue, marginTop: tokens.spacingVerticalXXS },
  chip: { marginRight: tokens.spacingHorizontalXS },
});

const SEVERITY_LABELS: Record<BroadcastSeverity, string> = {
  info: 'Info',
  warning: 'Warning',
  critical: 'Critical',
};

// =============================================================================
// Page
// =============================================================================

export function BroadcastsPage() {
  const styles = useStyles();
  const queryClient = useQueryClient();

  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<BroadcastSeverity>('info');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [endsAt, setEndsAt] = useState(defaultEnd());
  const [dismissible, setDismissible] = useState(true);
  const [everyone, setEveryone] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const roleGroups = useMemo(() => groupedVeraRoles(), []);

  const listQuery = useQuery({ queryKey: QUERY_KEY, queryFn: fetchBroadcasts });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    // The composer's own banner should update without waiting for the poll.
    void queryClient.invalidateQueries({ queryKey: BROADCAST_QUERY_KEY });
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await bffFetch('/api/shell/broadcasts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          severity,
          linkUrl: linkUrl.trim() || null,
          linkLabel: linkLabel.trim() || null,
          audienceKind: everyone ? 'everyone' : 'roles',
          audienceRoles: everyone ? [] : roles,
          // Read as Eastern, not as the author's browser timezone.
          endsAt: easternWallToIso(endsAt) ?? new Date(endsAt).toISOString(),
          dismissible,
        }),
      });
      await expectOk(res);
    },
    onSuccess: () => {
      setMessage('');
      setLinkUrl('');
      setLinkLabel('');
      setRoles([]);
      setEveryone(true);
      setSeverity('info');
      setEndsAt(defaultEnd());
      setError(null);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  const expireMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await bffFetch(`/api/shell/broadcasts/${id}/expire`, { method: 'POST' });
      await expectOk(res);
    },
    onSuccess: refresh,
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await bffFetch(`/api/shell/broadcasts/${id}`, { method: 'DELETE' });
      await expectOk(res);
    },
    onSuccess: refresh,
    onError: (err: Error) => setError(err.message),
  });

  const toggleRole = (value: string, checked: boolean) => {
    setRoles((prev) => (checked ? [...prev, value] : prev.filter((r) => r !== value)));
  };

  const tone = SEVERITY_COLORS[severity];
  const canSubmit =
    message.trim().length > 0 &&
    message.length <= MAX_MESSAGE_CHARS &&
    endsAt.length > 0 &&
    (everyone || roles.length > 0);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <AlertRegular className={styles.titleIcon} />
        <Title2>Broadcasts</Title2>
      </div>
      <Body1 className={styles.subtitle}>
        A message strip under the Vera header, for everyone or for chosen roles. It appears
        within a minute and disappears on its own at the end date.
      </Body1>

      <MessageBar intent="warning" className={styles.warning}>
        <MessageBarBody>
          <MessageBarTitle>No PHI, member data, or group specifics.</MessageBarTitle>
          A broadcast reaches an entire audience with no per-message review, and a role like
          Operations User is a wide net. Put the detail behind a link instead.
        </MessageBarBody>
      </MessageBar>

      {error && (
        <MessageBar intent="error" className={styles.warning}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <Card className={styles.composer}>
        <Subtitle2>New broadcast</Subtitle2>

        <Field label="Message" required style={{ marginTop: tokens.spacingVerticalM }}>
          <Textarea
            value={message}
            onChange={(_, d) => setMessage(d.value.slice(0, MAX_MESSAGE_CHARS))}
            placeholder="Vera is deploying at 6pm. Expect a brief sign-out."
            resize="vertical"
          />
          <Caption1 className={styles.counter}>
            {message.length} / {MAX_MESSAGE_CHARS}
          </Caption1>
        </Field>

        <div className={styles.row}>
          <Field label="Severity" className={styles.grow}>
            <Dropdown
              className={styles.grow}
              selectedOptions={[severity]}
              value={SEVERITY_LABELS[severity]}
              onOptionSelect={(_, d) => setSeverity(d.optionValue as BroadcastSeverity)}
            >
              <Option value="info">Info</Option>
              <Option value="warning">Warning</Option>
              <Option value="critical">Critical</Option>
            </Dropdown>
          </Field>

          <Field
            label="Ends"
            required
            hint="Required, and Eastern. Broadcasts always expire."
            className={styles.grow}
          >
            <Input
              type="datetime-local"
              value={endsAt}
              onChange={(_, d) => setEndsAt(d.value)}
            />
          </Field>
        </div>

        <div className={styles.row}>
          <Field
            label="Link (optional)"
            hint="An internal path like /academy/learn, or an https:// URL."
            className={styles.grow}
          >
            <Input
              value={linkUrl}
              onChange={(_, d) => setLinkUrl(d.value)}
              placeholder="/academy/learn"
            />
          </Field>
          <Field label="Link label" className={styles.grow}>
            <Input
              value={linkLabel}
              onChange={(_, d) => setLinkLabel(d.value)}
              placeholder="Open"
              disabled={!linkUrl.trim()}
            />
          </Field>
        </div>

        <div className={styles.row}>
          <Switch
            checked={everyone}
            onChange={(_, d) => setEveryone(d.checked)}
            label={everyone ? 'Everyone in Vera' : 'Only the roles below'}
          />
          <Switch
            checked={dismissible}
            onChange={(_, d) => setDismissible(d.checked)}
            label={dismissible ? 'People can dismiss it' : 'Cannot be dismissed'}
          />
        </div>

        {!everyone && (
          <div className={styles.roleGrid}>
            {roleGroups.map((group) => (
              <div key={group.group}>
                <Caption1 style={{ fontWeight: 600 }}>{group.group}</Caption1>
                {group.roles.map((role) => (
                  <Checkbox
                    key={role.value}
                    label={role.displayName}
                    checked={roles.includes(role.value)}
                    onChange={(_, d) => toggleRole(role.value, Boolean(d.checked))}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        <Caption1 style={{ marginTop: tokens.spacingVerticalL, display: 'block' }}>
          Preview
        </Caption1>
        <div
          className={styles.preview}
          style={{ backgroundColor: tone.bg, color: tone.fg }}
        >
          <AlertRegular fontSize={16} />
          <span>{message || 'Your message appears here.'}</span>
          {linkUrl.trim() && <strong>{linkLabel.trim() || 'Open'}</strong>}
        </div>

        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={!canSubmit || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? <Spinner size="tiny" /> : 'Broadcast'}
          </Button>
          <Caption1>
            {everyone
              ? 'Goes to everyone signed in to Vera.'
              : `Goes to ${roles.length} role${roles.length === 1 ? '' : 's'}.`}
          </Caption1>
        </div>
      </Card>

      <Subtitle2>Recent broadcasts</Subtitle2>
      {listQuery.isLoading && <Spinner size="tiny" style={{ marginTop: 16 }} />}
      {listQuery.isError && (
        <MessageBar intent="error" style={{ marginTop: 16 }}>
          <MessageBarBody>{(listQuery.error as Error).message}</MessageBarBody>
        </MessageBar>
      )}
      {listQuery.data && listQuery.data.length === 0 && (
        <Body1 className={styles.meta}>Nothing broadcast yet.</Body1>
      )}

      <div className={styles.list} style={{ marginTop: tokens.spacingVerticalM }}>
        {(listQuery.data ?? []).map((b) => (
          <Card key={b.id} className={styles.item}>
            <div className={styles.itemBody}>
              <Body1>{b.message}</Body1>
              <Caption1 className={styles.meta}>
                <Badge
                  appearance="tint"
                  color={isLive(b) ? 'success' : 'informative'}
                  className={styles.chip}
                >
                  {isLive(b) ? 'Live' : 'Ended'}
                </Badge>
                <Badge appearance="outline" className={styles.chip}>
                  {SEVERITY_LABELS[b.severity]}
                </Badge>
                {b.audienceKind === 'everyone'
                  ? 'Everyone'
                  : b.audienceRoles.join(', ')}
                {' · ends '}
                {formatWhen(b.endsAt)}
                {b.createdByName ? ` · ${b.createdByName}` : ''}
                {b.revision > 1 ? ` · edited (v${b.revision})` : ''}
              </Caption1>
            </div>
            {isLive(b) && (
              <Button
                appearance="subtle"
                icon={<PauseRegular />}
                title="End it now, keep the record"
                onClick={() => expireMutation.mutate(b.id)}
              >
                End now
              </Button>
            )}
            <Button
              appearance="subtle"
              icon={<DeleteRegular />}
              title="Remove from the list"
              onClick={() => deleteMutation.mutate(b.id)}
            />
          </Card>
        ))}
      </div>
    </div>
  );
}

export default BroadcastsPage;
